/**
 * Icon Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatCornerRadiusFormat,
  formatFromVariableSelect,
  formatFromVariableText,
  formatHorizontalAlignmentFormat,
  formatSpacingFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseCornerRadiusFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableSelect,
  parseToVariableText,
} from '../../parser-utils'
import type {
  HorizontalAlignment,
  ResponsiveValue,
  SpacingInputValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import iconTemplate from './icon.template.json'

const ICON_DEFAULT_TEMPLATE = iconTemplate as unknown as IIconComponentSettings

export interface IIconComponentSettings {
  // Data settings
  'icon-button-data-icon': VariableSelectValue
  'icon-button-data-icon-active'?: VariableSelectValue
  'icon-button-data-badge'?: VariableTextValue
  'icon-button-data-toggle'?: VariableSelectValue

  // Design settings
  'icon-button-design-scheme'?: any
  'icon-button-design-color-variant'?: string
  'icon-button-design-icon-color-variant'?: string
  'icon-button-design-icon-inactive-color'?: any
  'icon-button-design-icon-size'?: ResponsiveValue<number[]>
  'icon-button-design-icon-weight'?: any
  'icon-button-design-icon-grade'?: any
  'icon-button-design-variant'?: string
  'icon-button-design-toggled-variant'?: string
  'icon-button-design-shape'?: string
  'icon-button-design-h-align'?: ResponsiveValue<HorizontalAlignment>
  'icon-button-design-margin'?: ResponsiveValue<SpacingInputValue>
  'icon-button-design-padding'?: ResponsiveValue<SpacingInputValue>
  'icon-button-design-group-color'?: any
  'icon-button-design-active-color'?: any
  'icon-button-design-active-color-solid'?: any
  'icon-button-design-inactive-color'?: any
  'icon-button-design-inactive-color-solid'?: any
  'icon-button-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'

  // Actions
  'icon-button-action-click'?: BuilderActions
}

export interface IIconComponent extends IBuilderComponent {
  componentId: 'icon'
  groupId: 'button'
  settings: IIconComponentSettings
}

export interface AiIconInput {
  icon?: string // Format: "icon:add" or "icon:home"
  iconActive?: string // Format: "icon:check" or "icon:star"
  badge?: string[] // Format: ["text:3"] or ["text:New"]
  toggle?: string // Format: "icon:toggle" or select format
  size?: string | number
  cornerRadius?: string
  alignment?: string // Format: "h:center" or "h:start ta:h:center"
  margin?: string
  padding?: string
  activeColor?: string
  inactiveColor?: string
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
  onClick?: BuilderActions
}

export class IconComp {
  getTemplate(): IIconComponentSettings {
    return ICON_DEFAULT_TEMPLATE
  }

  /**
   * format icon component settings to AI icon input
   */
  format(settings: IIconComponentSettings): AiIconInput {
    const template = this.getTemplate()
    const result: AiIconInput = {}

    // Icon
    if (settings['icon-button-data-icon']) {
      const icon = formatFromVariableSelect(settings['icon-button-data-icon'])
      if (icon) {
        result.icon = icon
      }
    }

    // Icon Active
    if (settings['icon-button-data-icon-active']) {
      const iconActive = formatFromVariableSelect(settings['icon-button-data-icon-active'])
      if (iconActive && iconActive !== formatFromVariableSelect(template['icon-button-data-icon-active'])) {
        result.iconActive = iconActive
      }
    }

    // Badge
    if (settings['icon-button-data-badge']) {
      const badge = formatFromVariableText(settings['icon-button-data-badge'])
      if (badge.length > 0 && badge[0] !== 'text:') {
        result.badge = badge
      }
    }

    // Toggle
    if (settings['icon-button-data-toggle']) {
      const toggle = formatFromVariableSelect(settings['icon-button-data-toggle'])
      if (toggle) {
        result.toggle = toggle
      }
    }

    // Size
    if (settings['icon-button-design-icon-size']) {
      const sizeValue = settings['icon-button-design-icon-size'].phone?.[0]
      const templateSize = template['icon-button-design-icon-size']?.phone?.[0]
      if (sizeValue !== undefined && sizeValue !== templateSize) {
        result.size = sizeValue
      }
    }

    // Corner Radius
    const cornerRadius = formatCornerRadiusFormat(settings['icon-button-design-shape'])
    if (cornerRadius && cornerRadius !== template['icon-button-design-shape']) {
      result.cornerRadius = cornerRadius
    }

    // Alignment
    const alignment = formatHorizontalAlignmentFormat(settings['icon-button-design-h-align'])
    if (alignment) {
      result.alignment = alignment
    }

    // Margin
    const margin = formatSpacingFormat(settings['icon-button-design-margin'])
    if (margin) {
      result.margin = margin
    }

    // Padding
    const padding = formatSpacingFormat(settings['icon-button-design-padding'])
    if (padding) {
      result.padding = padding
    }

    // Active Color
    const activeColor = formatColorFormat({
      colorType: settings['icon-button-design-active-color'],
      colorSolid: settings['icon-button-design-active-color-solid'],
    })
    if (activeColor) {
      result.activeColor = activeColor
    }

    // Inactive Color
    const inactiveColor = formatColorFormat({
      colorType: settings['icon-button-design-inactive-color'],
      colorSolid: settings['icon-button-design-inactive-color-solid'],
    })
    if (inactiveColor) {
      result.inactiveColor = inactiveColor
    }

    // Hover Effect
    if (settings['icon-button-design-hover-effect']) {
      const hoverEffect = settings['icon-button-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    // Actions (copied as-is)
    if (settings['icon-button-action-click']) {
      result.onClick = settings['icon-button-action-click'] as any
    }

    return result
  }

  /**
   * parse AI icon input to icon component settings
   */
  parse(input: AiIconInput): IIconComponentSettings {
    const result = deepMerge(ICON_DEFAULT_TEMPLATE, {})

    // Data - Icon
    if (input.icon) {
      result['icon-button-data-icon'] = parseToVariableSelect(input.icon)
    }

    // Data - Icon Active
    if (input.iconActive) {
      result['icon-button-data-icon-active'] = parseToVariableSelect(input.iconActive)
    }

    // Data - Badge
    if (input.badge) {
      result['icon-button-data-badge'] = parseToVariableText(input.badge)
    }

    // Data - Toggle
    if (input.toggle) {
      result['icon-button-data-toggle'] = parseToVariableSelect(input.toggle)
    }

    // Design - Size
    if (input.size !== undefined) {
      if (typeof input.size === 'string') {
        // Parse responsive format if string
        result['icon-button-design-icon-size'] = parseResponsiveFormat(input.size, (val) => {
          // Extract number from string like "48px" -> [48]
          const numMatch = val.match(/(\d+)/)
          return numMatch ? [parseInt(numMatch[1])] : [48]
        })
      } else {
        // Single number value
        result['icon-button-design-icon-size'] = {
          phone: [input.size],
          'phone-landscape': [input.size],
          tablet: [input.size],
          'tablet-landscape': [input.size],
          desktop: [input.size],
          tv: [input.size],
        }
      }
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      result['icon-button-design-shape'] = parseCornerRadiusFormat(input.cornerRadius).phone
    }

    // Design - Alignment
    if (input.alignment) {
      result['icon-button-design-h-align'] = parseAlignmentFormat(input.alignment).hAlign
    }

    // Design - Margin
    if (input.margin) {
      result['icon-button-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Padding
    if (input.padding) {
      result['icon-button-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Active Color
    if (input.activeColor) {
      // Active color uses ColorAdaptiveValue format
      const colorParsed = parseColorFormat(input.activeColor)
      result['icon-button-design-active-color'] = colorParsed.colorType
      result['icon-button-design-active-color-solid'] = colorParsed.colorSolid
    }

    // Design - Inactive Color
    if (input.inactiveColor) {
      // Inactive color uses ColorAdaptiveValue format
      const colorParsed = parseColorFormat(input.inactiveColor)
      result['icon-button-design-inactive-color'] = colorParsed.colorType
      result['icon-button-design-inactive-color-solid'] = colorParsed.colorSolid
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['icon-button-design-hover-effect'] = input.hoverEffect
    }

    // Actions - Click
    if (input.onClick) {
      result['icon-button-action-click'] = input.onClick
    }

    return result
  }
}
